import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Flex,
  Icon,
  Skeleton,
  SkeletonText,
  Spacer,
  Tag,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import React from "react";
import {
  RiFlaskLine,
  RiInformationLine,
  RiLightbulbLine,
  RiMagicLine,
} from "react-icons/ri";
import { DocumentAIResults } from "src/hooks/useDocumentAI";
import { useOrganization } from "src/hooks/useOrganization";
import * as Url from "src/services/url";
import { MaybeLink } from "../Links/MaybeLink";

export interface AvelaIntelligenceAlertProps {
  /** DocumentAI results data */
  aiResults: DocumentAIResults | null;
  /** Loading state */
  isLoading?: boolean;
  /** Error state */
  hasError?: boolean;
}

/**
 * Alert component that displays overall Avela Intelligence status.
 * Shows different messages based on AI results availability and content.
 */
export const AvelaIntelligenceAlert: React.FC<AvelaIntelligenceAlertProps> = ({
  aiResults,
  isLoading = false,
  hasError = false,
}) => {
  const organization = useOrganization();
  const avelaLabsUrl = organization
    .map((org) => Url.OrgAdmin.AvelaLabs.aboutAvelaLabs(org))
    .withDefault(undefined);

  // Don't show anything if there's an error
  if (hasError) {
    return null;
  }

  // Show skeleton only while the hook is still waiting/polling
  if (isLoading) {
    return (
      <Alert
        status="info"
        borderRadius="md"
        mb={4}
        bg="purple.50"
        borderColor="purple.200"
        border="1px solid"
        position="relative"
      >
        <AlertIcon as={RiMagicLine} color="purple.500" boxSize={6} mr={3} />
        <Box flex="1">
          <AlertTitle
            fontSize="16px"
            fontWeight="700"
            color="gray.800"
            lineHeight="24px"
          >
            <Skeleton height="20px" width="200px" />
          </AlertTitle>
          <AlertDescription
            fontSize="16px"
            color="gray.700"
            mt={1}
            lineHeight="24px"
          >
            <SkeletonText mt="2" noOfLines={1} spacing="4" skeletonHeight="4" />
          </AlertDescription>
        </Box>
      </Alert>
    );
  }

  // Hide for missing results or terminal non-success (FAILED / ERROR / etc.)
  if (!aiResults || aiResults.status !== "COMPLETED") {
    return null;
  }

  // Check if AI found any results
  const hasResults =
    aiResults.validation_results?.results &&
    aiResults.validation_results.results.length > 0;

  // Count different types of matches for more detailed messaging
  const getResultsSummary = () => {
    if (!hasResults) {
      return {
        type: "no_results",
        title: "Avela Intelligence",
        description: "No matching information found in this document.",
        status: "info" as const,
      };
    }

    const results = aiResults.validation_results!.results;
    const highConfidence = results.filter((r) => r.certainty >= 85).length;
    const mediumConfidence = results.filter(
      (r) => r.certainty >= 50 && r.certainty < 85
    ).length;
    const lowConfidence = results.filter((r) => r.certainty < 50).length;

    if (highConfidence > 0 || mediumConfidence > 0) {
      return {
        type: "found_results",
        title: "Avela Intelligence found something",
        description:
          "Avela Intelligence has scanned this document and found information.",
        status: "info" as const,
        details: {
          highConfidence,
          mediumConfidence,
          lowConfidence,
          total: results.length,
        },
      };
    }

    return {
      type: "low_confidence",
      title: "Avela Intelligence",
      description:
        "Some information was detected but with low confidence. Please verify manually.",
      status: "warning" as const,
    };
  };

  const summary = getResultsSummary();

  return (
    <Alert
      status="info"
      borderRadius="md"
      mb={4}
      bg="primary.50"
      borderColor="primary.200"
      border="1px solid"
      position="relative"
    >
      <Flex direction="row" gap={2}>
        <AlertIcon as={RiMagicLine} color="primary.500" boxSize={6} mr={3} />
        <Box flex="1">
          <AlertTitle
            fontSize="16px"
            fontWeight="700"
            color="gray.800"
            lineHeight="24px"
          >
            {summary.title}
          </AlertTitle>
          <AlertDescription
            fontSize="16px"
            color="gray.700"
            mt={1}
            lineHeight="24px"
          >
            {summary.description}
          </AlertDescription>
        </Box>

        {/* Info tooltip on the right */}
        <Flex direction="column" alignItems="flex-end">
          <Tooltip
            label={
              <Box p={3}>
                {/* Purple "Tip" badge with icon */}
                <Flex
                  align="center"
                  gap={1}
                  bg="primary.500"
                  color="white"
                  px={2}
                  py={1}
                  borderRadius="md"
                  fontSize="xs"
                  fontWeight="600"
                  mb={3}
                  display="inline-flex"
                  width="fit-content"
                >
                  <RiLightbulbLine size={12} />
                  <Text>Tip</Text>
                </Flex>
                <Text fontWeight="semibold" mb={2} fontSize="sm">
                  Avela Intelligence
                </Text>
                <Text fontSize="sm" lineHeight="1.4">
                  Avela Intelligence will review documents for this verification
                  and identify important information. When successful, Avela
                  Intelligence will suggest the correct information for your
                  records.
                </Text>
              </Box>
            }
            placement="top-end"
            hasArrow
            bg="white"
            color="gray.800"
            borderColor="gray.200"
            borderWidth="1px"
            borderRadius="lg"
            shadow="lg"
            maxW="280px"
            offset={[0, 10]}
          >
            <Box
              as="button"
              position="absolute"
              top={3}
              right={3}
              _hover={{ opacity: 0.8 }}
              transition="opacity 0.2s"
            >
              <RiInformationLine
                size={20}
                color="primary.900"
                aria-label="Information about Avela Intelligence"
              />
            </Box>
          </Tooltip>
          <Spacer />
          <MaybeLink to={avelaLabsUrl} isExternal>
            <Tag size="sm" variant="outline" colorScheme="purple" gap={1}>
              <Icon as={RiFlaskLine} />
              <Text>Beta</Text>
            </Tag>
          </MaybeLink>
        </Flex>
      </Flex>
    </Alert>
  );
};
